import React from 'react'
import { useSession, isAdmin, isManager } from '@slerp/accounts'
import { Tabs, Row, Col, Divider } from 'antd'
import { LocationSettingProps } from './Settings'
import DeliverySettings from './SameDaySettings/DeliverySettings'
import PickupSettings from './SameDaySettings/PickupSettings'
import Details from './SameDaySettings/Details'
import PreparationTime from './SameDaySettings/PreparationTime'

import { PageTooltip } from '../Widgets'
import { useHistory, useParams } from 'react-router-dom'

const { TabPane } = Tabs

const SameDaySettings = (props: LocationSettingProps) => {
  const history = useHistory()
  const { user } = useSession()
  const { subtab } = useParams<{ subtab: string }>()
  const { store, merchant, refetch } = props
  const canManage = isAdmin(user) || isManager(user)

  const TABS = [
    {
      label: 'Order Types',
      key: 'order-types',
      content: (
        <Details {...{ store, merchant, refetch, disabled: !canManage }} />
      ),
      tooltip: (
        <PageTooltip title='Related articles from our Help Centre'>
          <a
            href='https://support.slerp.com/en_us/order-types-Hk5at4p_'
            target='_blank'
            rel='noopener noreferrer'
          >
            Order Types
          </a>
          <a
            href='https://support.slerp.com/knowledge/same-day-order-settings'
            target='_blank'
            rel='noopener noreferrer'
          >
            Same Day (ASAP)
          </a>
          <a
            href='https://support.slerp.com/knowledge/same-day-order-settings'
            target='_blank'
            rel='noopener noreferrer'
          >
            Same Day (Scheduled)
          </a>
        </PageTooltip>
      )
    },
    {
      label: 'Delivery',
      key: 'delivery',
      content: (
        <DeliverySettings
          {...{ store, merchant, refetch, disabled: !canManage }}
        />
      ),
      tooltip: (
        <PageTooltip title='Related articles from our Help Centre'>
          <a
            href='https://support.slerp.com/knowledge/custom-delivery-radius'
            target='_blank'
            rel='noopener noreferrer'
          >
            Custom Radius (polygon)
          </a>
          <a
            href='https://support.slerp.com/knowledge/how-is-a-vehicle-chosen-for-an-order'
            target='_blank'
            rel='noopener noreferrer'
          >
            Delivery methods & vehicle allocation
          </a>
          <a
            href='https://support.slerp.com/knowledge/delivery-costs'
            target='_blank'
            rel='noopener noreferrer'
          >
            Delivery Costs
          </a>
          <a
            href='https://support.slerp.com/knowledge/fulfillment-types'
            target='_blank'
            rel='noopener noreferrer'
          >
            Fulfilment Types
          </a>
          <a
            href='https://support.slerp.com/knowledge/intro-to-delivery'
            target='_blank'
            rel='noopener noreferrer'
          >
            Local & Nationwide Delivery
          </a>
          <a
            href='https://support.slerp.com/en_us/order-limit-by-time-slot-BkFv9LjTO'
            target='_blank'
            rel='noopener noreferrer'
          >
            Orders by time slot
          </a>
        </PageTooltip>
      )
    },
    {
      label: 'Pickup',
      key: 'pickup',
      content: (
        <PickupSettings
          {...{ store, merchant, refetch, disabled: !canManage }}
        />
      ),
      tooltip: (
        <PageTooltip title='Related articles from our Help Centre'>
          <a
            href='https://support.slerp.com/knowledge/set-time-slots-for-same-day-orders'
            target='_blank'
            rel='noopener noreferrer'
          >
            Collection & Pick-up Timings
          </a>
          <a
            href='https://support.slerp.com/knowledge/fulfillment-types'
            target='_blank'
            rel='noopener noreferrer'
          >
            Fulfillment Types
          </a>
          <a
            href='https://support.slerp.com/en_us/order-limit-by-time-slot-BkFv9LjTO'
            target='_blank'
            rel='noopener noreferrer'
          >
            Orders by time slot
          </a>
          <a
            href='https://support.slerp.com/en_us/intro-to-pick-up-BknFPoVTO'
            target='_blank'
            rel='noopener noreferrer'
          >
            Pick-up & Collections
          </a>
        </PageTooltip>
      )
    },
    {
      label: 'Preparation Time',
      key: 'preparation-time',
      content: (
        <PreparationTime {...{ store, disabled: !canManage, merchant }} />
      ),
      tooltip: (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '394px' }}>
            <PageTooltip>
              <span>
                Preparation time is the time your team needs to get an order
                ready for fulfillment. Adjust the sliders to reflect how long it
                typically takes during each activity mode.
                <br />
                <br /> For example, prep time is set to 30 minutes for busy mode
                so customers won’t be able to place orders for the next 30
                minute, helping prevent last-minute orders when your kitchen is
                at capacity.
                <br />
                <br />
                Learn more in the{' '}
                <a
                  href='https://support.slerp.com/knowledge/changing-your-preparation-time'
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{
                    textDecoration: 'underline',
                    color: '#4A4A68',
                    fontWeight: 500,
                    fontSize: '13px'
                  }}
                >
                  Help Centre Article
                </a>
              </span>
            </PageTooltip>
          </div>

          <PageTooltip>
            <span>
              Larger orders can have extra prep time added on top of the default
              time for the current prep mode. <br />
              <br /> For example, if busy mode is 30 minutes and the order is
              over £100, an extra 30 minutes can be added for a total of 60
              minutes. <br />
              <br /> Once set up, this adjusts automatically based on the basket
              value <strong>before any discounts are applied</strong>.
            </span>
          </PageTooltip>
        </div>
      )
    }
  ]

  return (
    <>
      <Tabs
        activeKey={subtab || 'order-types'}
        animated={{ inkBar: true, tabPane: true }}
        onChange={(e: string) =>
          history.push(`/location/${store.slug}/settings/same-day/${e}`)
        }
      >
        {TABS.map((tab) => (
          <TabPane tab={tab.label} key={tab.key}>
            <Row gutter={32} className='_mt-24 _pl-16'>
              <Col span={tab.key === 'preparation-time' ? 12 : 15}>
                {tab.content}
              </Col>
              <Col span={1}>
                <Divider type='vertical' />
              </Col>
              <Col span={tab.key === 'preparation-time' ? 10 : 8}>
                {tab.tooltip}
              </Col>
            </Row>
          </TabPane>
        ))}
      </Tabs>
    </>
  )
}
export default SameDaySettings
